import type { CSSProperties, ReactNode } from 'react';
import type { IconKey, icon3dKeys } from '@agency/contracts';

/**
 * Public UI primitives after halo-lab.com (decision 0004). Server-safe: no state, no effects.
 * Assets live in apps/web/public/visual (the admin preview serves the same folder).
 */

const VISUAL = '/visual';
const SAFE_HREF = /^(#[\w-]*|\/(?!\/)[^\s]*|https:\/\/[^\s]+)$/;
/** Defence in depth: legacy or hand-edited content can never render an unsafe link. */
export { blockMessages, caseStudyAction } from './messages.ts';
export const safeHref = (h: string) => (SAFE_HREF.test(h) ? h : '#');

/** Line icons are SVG masks so they take the text colour (currentColor) without inlining markup. */
const LINE_FILE: Partial<Record<string, string>> = { browser: 'web-design' };
export type UiIcon = IconKey | 'arrow-up-right' | 'chevron-down' | 'plus' | 'star' | 'mail' | 'calendar' | 'play' | 'pen';

export function LineIcon({ name, className = '' }: { name: UiIcon; className?: string }) {
  const file = LINE_FILE[name] ?? name;
  return <span className={`pb-ico ${className}`.trim()} style={{ '--ico': `url(${VISUAL}/icons/${file}.svg)` } as CSSProperties} aria-hidden="true" />;
}

/** Intrinsic sizes of the bundled 3D renders (prevents layout shift). */
const DIMS: Record<(typeof icon3dKeys)[number], [number, number]> = {
  cube: [130, 114], bolt: [130, 114], asterisk: [130, 114], seal: [200, 200],
  person: [120, 107], compass: [120, 107], sliders: [120, 107], puzzle: [120, 107], flow: [120, 107], database: [120, 107],
  diamond: [600, 358],
};
/** No runtime import from contracts: this module is also bundled into the header client island. */
export const is3d = (k: string | undefined): k is (typeof icon3dKeys)[number] => !!k && Object.prototype.hasOwnProperty.call(DIMS, k);

/** Decorative 3D glass render. Always alt="" (the adjacent text carries the meaning). */
export function Icon3d({ name, size, className = '', eager }: { name: (typeof icon3dKeys)[number]; size: number; className?: string; eager?: boolean }) {
  const [w, h] = DIMS[name];
  return (
    <picture className={`pb-3d ${className}`.trim()}>
      <source type="image/avif" srcSet={`${VISUAL}/icons-3d/${name}.avif`} />
      <img src={`${VISUAL}/icons-3d/${name}.webp`} alt="" width={size} height={Math.round((size * h) / w)} loading={eager ? 'eager' : 'lazy'} decoding="async" />
    </picture>
  );
}

/** One icon slot: 3D keys render the glass object, line keys render inside a 40px accent badge. */
export function ItemIcon({ name, size = 40 }: { name: IconKey; size?: number }) {
  if (is3d(name)) return <Icon3d name={name} size={size} className="pb-item-3d" />;
  return <span className="pb-badge" aria-hidden="true"><LineIcon name={name} /></span>;
}

export type PillVariant = 'primary' | 'warm' | 'outline-night' | 'outline-light' | 'white';

/** Pill button with the text roll (label rendered twice; the copy is aria-hidden) and an optional 32px icon chip. */
export function PillLabel({ label, icon }: { label: ReactNode; icon?: UiIcon | null }) {
  return (
    <>
      <span className="pb-btn__label">
        <span className="pb-btn__roll"><span>{label}</span><span aria-hidden="true">{label}</span></span>
      </span>
      {icon ? <span className="pb-btn__chip" aria-hidden="true"><LineIcon name={icon} /></span> : null}
    </>
  );
}

export function Pill({ href, label, variant = 'primary', icon = 'arrow-up-right', size, className = '', ...rest }: { href: string; label: ReactNode; variant?: PillVariant; icon?: UiIcon | null; size?: 'sm'; className?: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  return (
    <a className={`pb-btn pb-btn--${variant}${icon ? ' pb-btn--chip' : ''}${size ? ` pb-btn--${size}` : ''} ${className}`.trim()} href={safeHref(href)} {...rest}>
      <PillLabel label={label} icon={icon} />
    </a>
  );
}

/** Round ↗ link or decoration (40–64px). With a label it is a link; without, it is decorative. */
export function CircleArrow({ href, label, size = 40, className = '' }: { href?: string; label?: string; size?: 40 | 48 | 64; className?: string }) {
  const inner = <LineIcon name="arrow-up-right" />;
  return href && label ? (
    <a className={`pb-circle pb-circle--${size} ${className}`.trim()} href={safeHref(href)} aria-label={label}>{inner}</a>
  ) : (
    <span className={`pb-circle pb-circle--${size} ${className}`.trim()} aria-hidden="true">{inner}</span>
  );
}

/** Serif italic caption centered between two hairlines (home), or sans uppercase (service pages). */
export function Eyebrow({ children, serif, id }: { children: ReactNode; serif?: boolean; id?: string }) {
  return serif ? <p className="pb-eyebrow-serif" id={id}><span>{children}</span></p> : <p className="pb-eyebrow" id={id}>{children}</p>;
}

/** Renders `highlight` (a phrase from `text`) with gradient text; the full sentence stays real text. */
export function Highlighted({ text, highlight, tone = 'violet' }: { text: string; highlight?: string; tone?: 'violet' | 'warm' | 'tech' }) {
  const at = highlight ? text.indexOf(highlight) : -1;
  if (!highlight || at < 0) return <>{text}</>;
  return <>{text.slice(0, at)}<span className={`pb-grad pb-grad--${tone}`}>{highlight}</span>{text.slice(at + highlight.length)}</>;
}

/** Second half of a heading in gradient text (service-page section headings). Deterministic: splits at the middle word. */
export function SplitGradient({ text, tone }: { text: string; tone: 'warm' | 'tech' | 'violet' }) {
  const words = text.split(' ');
  if (words.length < 2) return <span className={`pb-grad pb-grad--${tone}`}>{text}</span>;
  const cut = Math.ceil(words.length / 2);
  return <>{words.slice(0, cut).join(' ')} <span className={`pb-grad pb-grad--${tone}`}>{words.slice(cut).join(' ')}</span></>;
}

/** The home hero object (hand with a glowing sphere), fixed aspect box, high fetch priority. */
export function HeroObject({ small }: { small?: boolean }) {
  return (
    <picture className={`pb-hero-object${small ? ' pb-hero-object--sm' : ''}`}>
      <source type="image/avif" srcSet={`${VISUAL}/hero/hand-720.avif 720w, ${VISUAL}/hero/hand.avif 1440w`} sizes={small ? '360px' : '(min-width: 1000px) 820px, 92vw'} />
      <img src={`${VISUAL}/hero/hand.webp`} srcSet={`${VISUAL}/hero/hand-720.webp 720w, ${VISUAL}/hero/hand.webp 1440w`} sizes={small ? '360px' : '(min-width: 1000px) 820px, 92vw'} alt="" width={1440} height={734} {...(small ? { loading: 'lazy' as const } : { fetchPriority: 'high' as const })} decoding="async" />
    </picture>
  );
}
